import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  IconButton,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Skeleton,
  Tooltip,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import TranslateIcon from '@mui/icons-material/Translate';
import { useNotification } from '../hooks/NotificationContext';
import PageHeader from '../components/common/PageHeader';
import FirewallVorlagenPanel from './FirewallVorlagenPanel';
import FirewallRisikoportsPanel from './FirewallRisikoportsPanel';
import ConfirmDialog from '../components/common/ConfirmDialog';
import {
  auditTypesService,
  schwachstellenklassenService,
  toolsService,
  risikoszenarienService,
} from '../services/lookupService';
import {
  AuditType,
  Schwachstellenklasse,
  Tool,
  Risikoszenario,
} from '../types';
import LookupTable from '../components/common/LookupTable';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
};

interface EditDialogProps {
  open: boolean;
  title: string;
  name: string;
  nameLabel: string;
  onNameChange: (value: string) => void;
  onClose: () => void;
  onSave: () => void;
  loading?: boolean;
  description?: string;
  onDescriptionChange?: (value: string) => void;
  descriptionLabel?: string;
}

const EditDialog: React.FC<EditDialogProps> = ({
  open,
  title,
  name,
  nameLabel,
  onNameChange,
  onClose,
  onSave,
  loading = false,
  description,
  onDescriptionChange,
  descriptionLabel,
}) => {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          margin="dense"
          label={nameLabel}
          fullWidth
          variant="outlined"
          value={name}
          onChange={(e) => onNameChange(e.target.value)}
          sx={{ mb: 2 }}
        />
        {onDescriptionChange && (
          <TextField
            margin="dense"
            label={descriptionLabel || 'Beschreibung'}
            fullWidth
            multiline
            rows={4}
            variant="outlined"
            value={description || ''}
            onChange={(e) => onDescriptionChange(e.target.value)}
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Abbrechen
        </Button>
        <Button onClick={onSave} variant="contained" disabled={loading}>
          {loading ? <CircularProgress size={24} /> : 'Speichern'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const VerwaltungPage: React.FC = () => {
  const { showSuccess, showError } = useNotification();
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(false);

  // Audit-Typen state
  const [auditTypes, setAuditTypes] = useState<AuditType[]>([]);
  const [auditTypesLoading, setAuditTypesLoading] = useState(true);
  const [auditTypesDialogOpen, setAuditTypesDialogOpen] = useState(false);
  const [editingAuditType, setEditingAuditType] = useState<AuditType | null>(null);
  const [auditTypeName, setAuditTypeName] = useState('');
  const [auditTypeKuerzel, setAuditTypeKuerzel] = useState('');
  const [auditTypeFarbe, setAuditTypeFarbe] = useState('#1976D2');

  // Schwachstellenklassen state
  const [schwachstellenklassen, setSchwachstellenklassen] = useState<Schwachstellenklasse[]>([]);
  const [schwachstellenklassenLoading, setSchwachstellenklassenLoading] = useState(true);
  const [schwachstellenklassenDialogOpen, setSchwachstellenklassenDialogOpen] = useState(false);
  const [editingSchwachstellenklasse, setEditingSchwachstellenklasse] = useState<Schwachstellenklasse | null>(null);
  const [schwachstellenklasseNameDe, setSchwachstellenklasseNameDe] = useState('');
  const [schwachstellenklasseNameEn, setSchwachstellenklasseNameEn] = useState('');
  const [schwachstellenklasseBeschreibungDe, setSchwachstellenklasseBeschreibungDe] = useState('');
  const [schwachstellenklasseBeschreibungEn, setSchwachstellenklasseBeschreibungEn] = useState('');
  const [translateNameDeToEnLoading, setTranslateNameDeToEnLoading] = useState(false);
  const [translateNameEnToDeLoading, setTranslateNameEnToDeLoading] = useState(false);
  const [translateBeschrDeToEnLoading, setTranslateBeschrDeToEnLoading] = useState(false);
  const [translateBeschrEnToDeLoading, setTranslateBeschrEnToDeLoading] = useState(false);

  // Tools state
  const [tools, setTools] = useState<Tool[]>([]);
  const [toolsLoading, setToolsLoading] = useState(true);
  const [toolsDialogOpen, setToolsDialogOpen] = useState(false);
  const [editingTool, setEditingTool] = useState<Tool | null>(null);
  const [toolName, setToolName] = useState('');

  // Risikoszenarien state
  const [risikoszenarien, setRisikoszenarien] = useState<Risikoszenario[]>([]);
  const [risikoszenarienLoading, setRisikoszenarienLoading] = useState(true);
  const [risikoszenarienDialogOpen, setRisikoszenarienDialogOpen] = useState(false);
  const [editingRisikoszenario, setEditingRisikoszenario] = useState<Risikoszenario | null>(null);
  const [risikoszenarioNameDe, setRisikoszenarioNameDe] = useState('');
  const [risikoszenarioNameEn, setRisikoszenarioNameEn] = useState('');
  const [translateRsNameDeToEnLoading, setTranslateRsNameDeToEnLoading] = useState(false);
  const [translateRsNameEnToDeLoading, setTranslateRsNameEnToDeLoading] = useState(false);

  // Delete confirmation state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<{
    type: 'auditType' | 'schwachstellenklasse' | 'tool' | 'risikoszenario';
    item: { id: number };
    displayName: string;
  } | null>(null);

  // Load data functions
  const loadAuditTypes = async () => {
    try {
      setAuditTypesLoading(true);
      const data = await auditTypesService.list();
      setAuditTypes(data);
    } catch {
      // Error displayed by axios interceptor
    } finally {
      setAuditTypesLoading(false);
    }
  };

  const loadSchwachstellenklassen = async () => {
    try {
      setSchwachstellenklassenLoading(true);
      const data = await schwachstellenklassenService.list();
      setSchwachstellenklassen(data);
    } catch {
      // Error displayed by axios interceptor
    } finally {
      setSchwachstellenklassenLoading(false);
    }
  };

  const loadTools = async () => {
    try {
      setToolsLoading(true);
      const data = await toolsService.list();
      setTools(data);
    } catch {
      // Error displayed by axios interceptor
    } finally {
      setToolsLoading(false);
    }
  };

  const loadRisikoszenarien = async () => {
    try {
      setRisikoszenarienLoading(true);
      const data = await risikoszenarienService.list();
      setRisikoszenarien(data);
    } catch {
      // Error displayed by axios interceptor
    } finally {
      setRisikoszenarienLoading(false);
    }
  };

  useEffect(() => {
    loadAuditTypes();
    loadSchwachstellenklassen();
    loadTools();
    loadRisikoszenarien();
  }, []);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // Audit-Typen handlers
  const handleAuditTypesAdd = () => {
    setEditingAuditType(null);
    setAuditTypeName('');
    setAuditTypeKuerzel('');
    setAuditTypeFarbe('#1976D2');
    setAuditTypesDialogOpen(true);
  };

  const handleAuditTypesEdit = (auditType: AuditType) => {
    setEditingAuditType(auditType);
    setAuditTypeName(auditType.name);
    setAuditTypeKuerzel(auditType.kuerzel || '');
    setAuditTypeFarbe(auditType.farbe || '#1976D2');
    setAuditTypesDialogOpen(true);
  };

  const handleAuditTypesSave = async () => {
    try {
      setLoading(true);
      const payload = { name: auditTypeName, kuerzel: auditTypeKuerzel || undefined, farbe: auditTypeFarbe || undefined };
      if (editingAuditType) {
        await auditTypesService.update(editingAuditType.id, payload);
        showSuccess('Audit-Typ erfolgreich aktualisiert');
      } else {
        await auditTypesService.create(payload as Omit<AuditType, 'id'>);
        showSuccess('Audit-Typ erfolgreich erstellt');
      }
      setAuditTypesDialogOpen(false);
      await loadAuditTypes();
    } catch {
      // Error displayed by axios interceptor
    } finally {
      setLoading(false);
    }
  };

  const handleAuditTypesDelete = (auditType: AuditType) => {
    setItemToDelete({ type: 'auditType', item: auditType, displayName: auditType.name });
    setDeleteDialogOpen(true);
  };

  // Schwachstellenklassen handlers
  const handleSchwachstellenklassenAdd = () => {
    setEditingSchwachstellenklasse(null);
    setSchwachstellenklasseNameDe('');
    setSchwachstellenklasseNameEn('');
    setSchwachstellenklasseBeschreibungDe('');
    setSchwachstellenklasseBeschreibungEn('');
    setSchwachstellenklassenDialogOpen(true);
  };

  const handleSchwachstellenklassenEdit = (schwachstellenklasse: Schwachstellenklasse) => {
    setEditingSchwachstellenklasse(schwachstellenklasse);
    setSchwachstellenklasseNameDe(schwachstellenklasse.name_de);
    setSchwachstellenklasseNameEn(schwachstellenklasse.name_en || '');
    setSchwachstellenklasseBeschreibungDe(schwachstellenklasse.allgemeine_beschreibung_de || '');
    setSchwachstellenklasseBeschreibungEn(schwachstellenklasse.allgemeine_beschreibung_en || '');
    setSchwachstellenklassenDialogOpen(true);
  };

  const handleSchwachstellenklassenSave = async () => {
    try {
      setLoading(true);
      const data: Partial<Schwachstellenklasse> = {
        name_de: schwachstellenklasseNameDe,
        name_en: schwachstellenklasseNameEn || undefined,
        allgemeine_beschreibung_de: schwachstellenklasseBeschreibungDe || undefined,
        allgemeine_beschreibung_en: schwachstellenklasseBeschreibungEn || undefined,
      };
      if (editingSchwachstellenklasse) {
        await schwachstellenklassenService.update(editingSchwachstellenklasse.id, data);
        showSuccess('Schwachstellenklasse erfolgreich aktualisiert');
      } else {
        await schwachstellenklassenService.create(data as Omit<Schwachstellenklasse, 'id'>);
        showSuccess('Schwachstellenklasse erfolgreich erstellt');
      }
      setSchwachstellenklassenDialogOpen(false);
      await loadSchwachstellenklassen();
    } catch {
      // Error displayed by axios interceptor
    } finally {
      setLoading(false);
    }
  };

  const handleSchwachstellenklassenDelete = (schwachstellenklasse: Schwachstellenklasse) => {
    setItemToDelete({ type: 'schwachstellenklasse', item: schwachstellenklasse, displayName: schwachstellenklasse.name_de });
    setDeleteDialogOpen(true);
  };

  const isAnyTranslateLoading = translateNameDeToEnLoading || translateNameEnToDeLoading || translateBeschrDeToEnLoading || translateBeschrEnToDeLoading;

  const handleTranslateSchwachstellenklasse = async (field: 'name' | 'beschreibung', direction: 'deToEn' | 'enToDe') => {
    const isDeToEn = direction === 'deToEn';
    const sourceText = field === 'name'
      ? (isDeToEn ? schwachstellenklasseNameDe.trim() : schwachstellenklasseNameEn.trim())
      : (isDeToEn ? schwachstellenklasseBeschreibungDe.trim() : schwachstellenklasseBeschreibungEn.trim());
    if (!sourceText) return;

    const setLoadingFn = field === 'name'
      ? (isDeToEn ? setTranslateNameDeToEnLoading : setTranslateNameEnToDeLoading)
      : (isDeToEn ? setTranslateBeschrDeToEnLoading : setTranslateBeschrEnToDeLoading);

    try {
      setLoadingFn(true);

      const result = await schwachstellenklassenService.translate(
        sourceText,
        isDeToEn ? 'de' : 'en',
        isDeToEn ? 'en' : 'de'
      );

      if (field === 'name') {
        if (isDeToEn) setSchwachstellenklasseNameEn(result.translated_text);
        else setSchwachstellenklasseNameDe(result.translated_text);
      } else {
        if (isDeToEn) setSchwachstellenklasseBeschreibungEn(result.translated_text);
        else setSchwachstellenklasseBeschreibungDe(result.translated_text);
      }
      showSuccess('Erfolgreich übersetzt');
    } catch {
      showError('Übersetzung fehlgeschlagen');
    } finally {
      setLoadingFn(false);
    }
  };

  // Tools handlers
  const handleToolsAdd = () => {
    setEditingTool(null);
    setToolName('');
    setToolsDialogOpen(true);
  };

  const handleToolsEdit = (tool: Tool) => {
    setEditingTool(tool);
    setToolName(tool.name);
    setToolsDialogOpen(true);
  };

  const handleToolsSave = async () => {
    try {
      setLoading(true);
      if (editingTool) {
        await toolsService.update(editingTool.id, { name: toolName });
        showSuccess('Tool erfolgreich aktualisiert');
      } else {
        await toolsService.create({ name: toolName });
        showSuccess('Tool erfolgreich erstellt');
      }
      setToolsDialogOpen(false);
      await loadTools();
    } catch {
      // Error displayed by axios interceptor
    } finally {
      setLoading(false);
    }
  };

  const handleToolsDelete = (tool: Tool) => {
    setItemToDelete({ type: 'tool', item: tool, displayName: tool.name });
    setDeleteDialogOpen(true);
  };

  // Risikoszenarien handlers
  const handleRisikoszenarienAdd = () => {
    setEditingRisikoszenario(null);
    setRisikoszenarioNameDe('');
    setRisikoszenarioNameEn('');
    setRisikoszenarienDialogOpen(true);
  };

  const handleRisikoszenarienEdit = (item: Risikoszenario) => {
    setEditingRisikoszenario(item);
    setRisikoszenarioNameDe(item.name_de);
    setRisikoszenarioNameEn(item.name_en || '');
    setRisikoszenarienDialogOpen(true);
  };

  const handleRisikoszenarienSave = async () => {
    try {
      setLoading(true);
      const data: Partial<Risikoszenario> = {
        name_de: risikoszenarioNameDe,
        name_en: risikoszenarioNameEn || undefined,
      };
      if (editingRisikoszenario) {
        await risikoszenarienService.update(editingRisikoszenario.id, data);
        showSuccess('Risikoszenario erfolgreich aktualisiert');
      } else {
        await risikoszenarienService.create(data as Omit<Risikoszenario, 'id'>);
        showSuccess('Risikoszenario erfolgreich erstellt');
      }
      setRisikoszenarienDialogOpen(false);
      await loadRisikoszenarien();
    } catch {
      // Error displayed by axios interceptor
    } finally {
      setLoading(false);
    }
  };

  const handleRisikoszenarienDelete = (item: Risikoszenario) => {
    setItemToDelete({ type: 'risikoszenario', item, displayName: item.name_de });
    setDeleteDialogOpen(true);
  };

  const isAnyRsTranslateLoading = translateRsNameDeToEnLoading || translateRsNameEnToDeLoading;

  const handleTranslateRisikoszenario = async (direction: 'deToEn' | 'enToDe') => {
    const isDeToEn = direction === 'deToEn';
    const sourceText = isDeToEn ? risikoszenarioNameDe.trim() : risikoszenarioNameEn.trim();
    if (!sourceText) return;

    const setLoadingFn = isDeToEn ? setTranslateRsNameDeToEnLoading : setTranslateRsNameEnToDeLoading;

    try {
      setLoadingFn(true);
      const result = await risikoszenarienService.translate(
        sourceText,
        isDeToEn ? 'de' : 'en',
        isDeToEn ? 'en' : 'de'
      );
      if (isDeToEn) setRisikoszenarioNameEn(result.translated_text);
      else setRisikoszenarioNameDe(result.translated_text);
      showSuccess('Erfolgreich übersetzt');
    } catch {
      showError('Übersetzung fehlgeschlagen');
    } finally {
      setLoadingFn(false);
    }
  };

  // Delete handler
  const handleDeleteConfirm = async () => {
    if (!itemToDelete) return;

    try {
      setLoading(true);
      switch (itemToDelete.type) {
        case 'auditType':
          await auditTypesService.delete(itemToDelete.item.id);
          showSuccess('Audit-Typ erfolgreich gelöscht');
          await loadAuditTypes();
          break;
        case 'schwachstellenklasse':
          await schwachstellenklassenService.delete(itemToDelete.item.id);
          showSuccess('Schwachstellenklasse erfolgreich gelöscht');
          await loadSchwachstellenklassen();
          break;
        case 'tool':
          await toolsService.delete(itemToDelete.item.id);
          showSuccess('Tool erfolgreich gelöscht');
          await loadTools();
          break;
        case 'risikoszenario':
          await risikoszenarienService.delete(itemToDelete.item.id);
          showSuccess('Risikoszenario erfolgreich gelöscht');
          await loadRisikoszenarien();
          break;
      }
      setDeleteDialogOpen(false);
      setItemToDelete(null);
    } catch {
      // Error displayed by axios interceptor
    } finally {
      setLoading(false);
    }
  };

  const sortedSchwachstellenklassen = useMemo(
    () => [...schwachstellenklassen].sort((a, b) => a.name_de.localeCompare(b.name_de, 'de', { sensitivity: 'base' })),
    [schwachstellenklassen]
  );

  const tabs = useMemo(() => {
    const list: { key: string; label: string; content: React.ReactNode }[] = [];

    list.push({
      key: 'audittypen',
      label: 'Audit-Typen',
      content: (
        <LookupTable
          items={auditTypes}
          onAdd={handleAuditTypesAdd}
          onEdit={handleAuditTypesEdit}
          onDelete={handleAuditTypesDelete}
          loading={auditTypesLoading}
          columns={[
            { label: 'Name', render: (item) => item.name },
            {
              label: 'Kürzel',
              render: (item) => item.kuerzel ? (
                <Box
                  component="span"
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    px: 1,
                    borderRadius: 1,
                    fontSize: '0.75rem',
                    fontWeight: 700,
                    color: '#fff',
                    backgroundColor: item.farbe || '#757575',
                    height: 24,
                  }}
                >
                  {item.kuerzel}
                </Box>
              ) : '-',
            },
            {
              label: 'Farbe',
              render: (item) => item.farbe ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box
                    sx={{
                      width: 20,
                      height: 20,
                      borderRadius: '50%',
                      backgroundColor: item.farbe,
                      border: '1px solid rgba(0,0,0,0.2)',
                    }}
                  />
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                    {item.farbe}
                  </Typography>
                </Box>
              ) : '-',
            },
          ]}
        />
      ),
    });

    list.push({
      key: 'schwachstellenklassen',
      label: 'Schwachstellenklassen',
      content: (
        <LookupTable
          items={sortedSchwachstellenklassen}
          onAdd={handleSchwachstellenklassenAdd}
          onEdit={handleSchwachstellenklassenEdit}
          onDelete={handleSchwachstellenklassenDelete}
          loading={schwachstellenklassenLoading}
          columns={[{ label: 'Name (DE)', render: (item) => item.name_de }, { label: 'Name (EN)', render: (item) => item.name_en || '–' }]}
        />
      ),
    });

    list.push({
      key: 'tools',
      label: 'Tools',
      content: (
        <LookupTable
          items={tools}
          onAdd={handleToolsAdd}
          onEdit={handleToolsEdit}
          onDelete={handleToolsDelete}
          loading={toolsLoading}
          columns={[{ label: 'Name', render: (item) => item.name }]}
        />
      ),
    });

    list.push({
      key: 'risikoszenarien',
      label: 'Risikoszenarien',
      content: (
        <LookupTable
          items={risikoszenarien}
          onAdd={handleRisikoszenarienAdd}
          onEdit={handleRisikoszenarienEdit}
          onDelete={handleRisikoszenarienDelete}
          loading={risikoszenarienLoading}
          columns={[{ label: 'Name (DE)', render: (item) => item.name_de }, { label: 'Name (EN)', render: (item) => item.name_en || '–' }]}
        />
      ),
    });

    list.push({
      key: 'firewall-vorlagen',
      label: 'Firewall-Kommentare',
      content: <FirewallVorlagenPanel />,
    });

    list.push({
      key: 'firewall-risikoports',
      label: 'Firewall-Risikoports',
      content: <FirewallRisikoportsPanel />,
    });

    return list;
  }, [
    auditTypes, auditTypesLoading,
    sortedSchwachstellenklassen, schwachstellenklassenLoading,
    tools, toolsLoading,
    risikoszenarien, risikoszenarienLoading,
  ]);




  const safeTabValue = tabValue >= tabs.length ? 0 : tabValue;

  return (
    <Box>
      <PageHeader title="Stammdaten" subtitle="Audit-Typen, Schwachstellenklassen, Tools, Risikoszenarien, Firewall-Kommentare und Firewall-Risikoports" breadcrumbs={[{ label: 'Stammdaten' }]} />
      
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={safeTabValue} onChange={handleTabChange} variant="scrollable" scrollButtons="auto">
          {tabs.map((tab) => (
            <Tab key={tab.key} label={tab.label} />
          ))}
        </Tabs>
      </Box>

      {tabs.map((tab, index) => (
        <TabPanel key={tab.key} value={safeTabValue} index={index}>
          {tab.content}
        </TabPanel>
      ))}

      {/* Audit-Typen Dialog */}
      <Dialog open={auditTypesDialogOpen} onClose={() => setAuditTypesDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingAuditType ? 'Audit-Typ bearbeiten' : 'Neuer Audit-Typ'}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Name"
            fullWidth
            variant="outlined"
            value={auditTypeName}
            onChange={(e) => setAuditTypeName(e.target.value)}
            sx={{ mb: 2 }}
          />
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
            <TextField
              margin="dense"
              label="Kürzel (1-4 Zeichen)"
              variant="outlined"
              value={auditTypeKuerzel}
              onChange={(e) => {
                if (e.target.value.length <= 4) setAuditTypeKuerzel(e.target.value.toUpperCase());
              }}
              inputProps={{ maxLength: 4 }}
              sx={{ width: 180 }}
              helperText={`${auditTypeKuerzel.length}/4 Zeichen`}
            />
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Farbe
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box
                  component="input"
                  type="color"
                  value={auditTypeFarbe}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setAuditTypeFarbe(e.target.value)}
                  sx={{
                    width: 44,
                    height: 44,
                    border: '2px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    cursor: 'pointer',
                    p: 0.25,
                    '&::-webkit-color-swatch-wrapper': { p: 0 },
                    '&::-webkit-color-swatch': { borderRadius: 0.5, border: 'none' },
                  }}
                />
                <TextField
                  size="small"
                  value={auditTypeFarbe}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (v.match(/^#[0-9A-Fa-f]{0,6}$/)) setAuditTypeFarbe(v);
                  }}
                  sx={{ width: 110, fontFamily: 'monospace' }}
                  inputProps={{ style: { fontFamily: 'monospace', fontSize: '0.85rem' } }}
                />
              </Box>
            </Box>
          </Box>
          {auditTypeKuerzel && (
            <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" color="text.secondary">Vorschau:</Typography>
              <Box
                component="span"
                sx={{
                  display: 'inline-block',
                  px: 1.25,
                  py: 0.5,
                  borderRadius: 1,
                  fontSize: '0.8rem',
                  fontWeight: 700,
                  color: '#fff',
                  backgroundColor: auditTypeFarbe || '#757575',
                  letterSpacing: '0.05em',
                }}
              >
                {auditTypeKuerzel}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAuditTypesDialogOpen(false)} disabled={loading}>
            Abbrechen
          </Button>
          <Button onClick={handleAuditTypesSave} variant="contained" disabled={loading || !auditTypeName.trim()}>
            {loading ? <CircularProgress size={24} /> : 'Speichern'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Schwachstellenklassen Dialog (multilingual DE/EN, zweispaltig) */}
      <Dialog open={schwachstellenklassenDialogOpen} onClose={() => setSchwachstellenklassenDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editingSchwachstellenklasse ? 'Schwachstellenklasse bearbeiten' : 'Neue Schwachstellenklasse'}</DialogTitle>
        <DialogContent>
          {/* Spalten-Ueberschriften */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1, mb: 1.5 }}>
            <Typography variant="subtitle2" sx={{ flex: 1, fontWeight: 600 }}>Deutsch</Typography>
            <Box sx={{ width: 72, textAlign: 'center', flexShrink: 0 }}>
              <Tooltip title="KI-gestützte Übersetzung" arrow>
                <TranslateIcon fontSize="small" color="action" />
              </Tooltip>
            </Box>
            <Typography variant="subtitle2" sx={{ flex: 1, fontWeight: 600 }}>English</Typography>
          </Box>

          {/* Name-Zeile */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TextField
              autoFocus
              label="Name"
              fullWidth
              variant="outlined"
              value={schwachstellenklasseNameDe}
              onChange={(e) => setSchwachstellenklasseNameDe(e.target.value)}
              sx={{ flex: 1 }}
            />
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, flexShrink: 0 }}>
              <Tooltip title="DE → EN übersetzen" arrow>
                <span>
                  <IconButton
                    size="small"
                    onClick={() => handleTranslateSchwachstellenklasse('name', 'deToEn')}
                    disabled={isAnyTranslateLoading || !schwachstellenklasseNameDe.trim()}
                  >
                    {translateNameDeToEnLoading ? <CircularProgress size={18} /> : <ArrowForwardIcon fontSize="small" />}
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="EN → DE übersetzen" arrow>
                <span>
                  <IconButton
                    size="small"
                    onClick={() => handleTranslateSchwachstellenklasse('name', 'enToDe')}
                    disabled={isAnyTranslateLoading || !schwachstellenklasseNameEn.trim()}
                  >
                    {translateNameEnToDeLoading ? <CircularProgress size={18} /> : <ArrowBackIcon fontSize="small" />}
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
            <TextField
              label="Name"
              fullWidth
              variant="outlined"
              value={schwachstellenklasseNameEn}
              onChange={(e) => setSchwachstellenklasseNameEn(e.target.value)}
              sx={{ flex: 1 }}
            />
          </Box>

          {/* Beschreibung-Zeile */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
            <TextField
              label="Allgemeine Beschreibung"
              fullWidth
              multiline
              rows={4}
              variant="outlined"
              value={schwachstellenklasseBeschreibungDe}
              onChange={(e) => setSchwachstellenklasseBeschreibungDe(e.target.value)}
              sx={{ flex: 1 }}
            />
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, flexShrink: 0 }}>
              <Tooltip title="DE → EN übersetzen" arrow>
                <span>
                  <IconButton
                    size="small"
                    onClick={() => handleTranslateSchwachstellenklasse('beschreibung', 'deToEn')}
                    disabled={isAnyTranslateLoading || !schwachstellenklasseBeschreibungDe.trim()}
                  >
                    {translateBeschrDeToEnLoading ? <CircularProgress size={18} /> : <ArrowForwardIcon fontSize="small" />}
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="EN → DE übersetzen" arrow>
                <span>
                  <IconButton
                    size="small"
                    onClick={() => handleTranslateSchwachstellenklasse('beschreibung', 'enToDe')}
                    disabled={isAnyTranslateLoading || !schwachstellenklasseBeschreibungEn.trim()}
                  >
                    {translateBeschrEnToDeLoading ? <CircularProgress size={18} /> : <ArrowBackIcon fontSize="small" />}
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
            <TextField
              label="Allgemeine Beschreibung"
              fullWidth
              multiline
              rows={4}
              variant="outlined"
              value={schwachstellenklasseBeschreibungEn}
              onChange={(e) => setSchwachstellenklasseBeschreibungEn(e.target.value)}
              sx={{ flex: 1 }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSchwachstellenklassenDialogOpen(false)} disabled={loading}>
            Abbrechen
          </Button>
          <Button onClick={handleSchwachstellenklassenSave} variant="contained" disabled={loading || !schwachstellenklasseNameDe.trim()}>
            {loading ? <CircularProgress size={24} /> : 'Speichern'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Tools Dialog */}
      <EditDialog
        open={toolsDialogOpen}
        title={editingTool ? 'Tool bearbeiten' : 'Neues Tool'}
        name={toolName}
        nameLabel="Name"
        onNameChange={setToolName}
        onClose={() => setToolsDialogOpen(false)}
        onSave={handleToolsSave}
        loading={loading}
      />

      {/* Risikoszenarien Dialog (multilingual DE/EN, zweispaltig) */}
      <Dialog open={risikoszenarienDialogOpen} onClose={() => setRisikoszenarienDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editingRisikoszenario ? 'Risikoszenario bearbeiten' : 'Neues Risikoszenario'}</DialogTitle>
        <DialogContent>
          {/* Spalten-Ueberschriften */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1, mb: 1.5 }}>
            <Typography variant="subtitle2" sx={{ flex: 1, fontWeight: 600 }}>Deutsch</Typography>
            <Box sx={{ width: 72, textAlign: 'center', flexShrink: 0 }}>
              <Tooltip title="KI-gestützte Übersetzung" arrow>
                <TranslateIcon fontSize="small" color="action" />
              </Tooltip>
            </Box>
            <Typography variant="subtitle2" sx={{ flex: 1, fontWeight: 600 }}>English</Typography>
          </Box>

          {/* Name-Zeile */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TextField
              autoFocus
              label="Name"
              fullWidth
              variant="outlined"
              value={risikoszenarioNameDe}
              onChange={(e) => setRisikoszenarioNameDe(e.target.value)}
              sx={{ flex: 1 }}
            />
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, flexShrink: 0 }}>
              <Tooltip title="DE → EN übersetzen" arrow>
                <span>
                  <IconButton
                    size="small"
                    onClick={() => handleTranslateRisikoszenario('deToEn')}
                    disabled={isAnyRsTranslateLoading || !risikoszenarioNameDe.trim()}
                  >
                    {translateRsNameDeToEnLoading ? <CircularProgress size={18} /> : <ArrowForwardIcon fontSize="small" />}
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="EN → DE übersetzen" arrow>
                <span>
                  <IconButton
                    size="small"
                    onClick={() => handleTranslateRisikoszenario('enToDe')}
                    disabled={isAnyRsTranslateLoading || !risikoszenarioNameEn.trim()}
                  >
                    {translateRsNameEnToDeLoading ? <CircularProgress size={18} /> : <ArrowBackIcon fontSize="small" />}
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
            <TextField
              label="Name"
              fullWidth
              variant="outlined"
              value={risikoszenarioNameEn}
              onChange={(e) => setRisikoszenarioNameEn(e.target.value)}
              sx={{ flex: 1 }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRisikoszenarienDialogOpen(false)} disabled={loading}>
            Abbrechen
          </Button>
          <Button onClick={handleRisikoszenarienSave} variant="contained" disabled={loading || !risikoszenarioNameDe.trim()}>
            {loading ? <CircularProgress size={24} /> : 'Speichern'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={deleteDialogOpen}
        title="Löschen bestätigen"
        message={`Möchten Sie "${itemToDelete?.displayName}" wirklich löschen?`}
        onConfirm={handleDeleteConfirm}
        onCancel={() => {
          setDeleteDialogOpen(false);
          setItemToDelete(null);
        }}
      />

    </Box>
  );
};

export default VerwaltungPage;
